import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TopicSelect } from "@/components/filters/TopicSelect";
import { DifficultySelect } from "@/components/filters/DifficultySelect";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import MCQForm from "./MCQForm";
import ExplanationGenerator from "./ExplanationGenerator";
import { MCQReviewDialog } from "./MCQReviewDialog";
import { MCQTable } from "./MCQTable";
import { BulkActionsToolbar } from "./BulkActionsToolbar";
import { BulkLabelActionsDialog } from "./BulkLabelActionsDialog";
import { MCQPreviewModal } from "./MCQPreviewModal";
import { useMCQFilters } from "@/hooks/useMCQFilters";
import { useAllMcqs } from "@/hooks/useAllMcqs";
import { RotateCcw } from "lucide-react";

const sb = supabase as unknown as SupabaseClient<any>;

type Topic = { id: string; name: string; parent_topic_id: string | null };

type Mcq = {
  id: string;
  topic_id: string | null;
  question: string;
  options: string[];
  correct_index: number;
  explanation?: string | null;
  difficulty: number;
  tags: string[] | null;
  review_status?: 'pending' | 'approved' | 'needs_revision';
  last_reviewed_at?: string | null;
  lifecycle_status?: 'active' | 'draft' | 'retired';
  retired_reason?: string | null;
  exam_labels?: Array<{
    id: string;
    name: string;
    slug: string;
    color: string;
  }>;
};

const fetchTopics = async (): Promise<Topic[]> => {
  const { data, error } = await sb.from("topics").select("id,name,parent_topic_id").order("name", { ascending: true });
  if (error) throw error;
  return data ?? [];
};

const MCQManager = () => {
  const qc = useQueryClient();
  const { data: topics = [] } = useQuery({ queryKey: ["topics"], queryFn: fetchTopics });
  const { data: rawMcqs = [], isLoading, error } = useAllMcqs();

  // Transform database format to frontend format
  const mcqs = useMemo(() => {
    return rawMcqs.map(row => ({
      id: row.id,
      topic_id: row.topic_id,
      question: row.stem,
      options: [
        row.option_a,
        row.option_b,
        row.option_c,
        row.option_d,
        row.option_e
      ].filter(Boolean),
      correct_index: ['A', 'B', 'C', 'D', 'E'].indexOf(row.correct_option),
      explanation: row.explanation,
      difficulty: row.difficulty || 3,
      tags: row.tags,
      review_status: (row.review_status as 'pending' | 'approved' | 'needs_revision') || 'pending',
      last_reviewed_at: row.last_reviewed_at,
      lifecycle_status: ((row as any).lifecycle_status as 'active' | 'draft' | 'retired') || 'active',
      retired_reason: (row as any).retired_reason ?? null,
      exam_labels: row.exam_labels
    }));
  }, [rawMcqs]);

  const byId = useMemo(() => new Map(topics.map((t) => [t.id, t])), [topics]);

  const [editing, setEditing] = useState<Mcq | null>(null);
  const [previewMcq, setPreviewMcq] = useState<Mcq | null>(null);
  
  const {
    filtered,
    query,
    setQuery,
    topicFilter,
    setTopicFilter,
    difficultyFilter,
    setDifficultyFilter,
    reviewStatusFilter,
    setReviewStatusFilter,
    examLabelFilter,
    setExamLabelFilter,
    quickFilter,
    setQuickFilter,
    resetFilters
  } = useMCQFilters(mcqs);

  type ExamLabel = { id: string; slug: string; name: string };
  const { data: labels = [] } = useQuery({
    queryKey: ["exam-labels"],
    queryFn: async (): Promise<ExamLabel[]> => {
      const { data, error } = await sb.from("exam_labels").select("id,slug,name").order("name", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const [createLabelIds, setCreateLabelIds] = useState<string[]>([]);
  const [editLabelIds, setEditLabelIds] = useState<string[]>([]);
  const [explanationMcq, setExplanationMcq] = useState<Mcq | null>(null);
  const [reviewMcq, setReviewMcq] = useState<Mcq | null>(null);
  const [selectedMcqIds, setSelectedMcqIds] = useState<Set<string>>(new Set());
  const [showBulkLabelDialog, setShowBulkLabelDialog] = useState(false);
  const [bulkLabelMode, setBulkLabelMode] = useState<'add' | 'remove'>('add');

  const handleBulkAddLabels = () => {
    setBulkLabelMode('add');
    setShowBulkLabelDialog(true);
  };
  
  const handleBulkRemoveLabels = () => {
    setBulkLabelMode('remove');
    setShowBulkLabelDialog(true);
  };
  
  const handleBulkComplete = () => {
    setSelectedMcqIds(new Set());
    setShowBulkLabelDialog(false);
  };

  const handleDuplicate = (mcq: Mcq) => {
    const duplicated: Omit<Mcq, 'id'> = {
      topic_id: mcq.topic_id,
      question: mcq.question + " (Copy)",
      options: [...mcq.options],
      correct_index: mcq.correct_index,
      explanation: mcq.explanation,
      difficulty: mcq.difficulty,
      tags: mcq.tags ? [...mcq.tags, "copy"] : ["copy"]
    };
    createMutation.mutate(duplicated);
  };

  type QuickFilterType = 'all' | 'pending' | 'needs_revision' | 'missing_explanation' | 'recent' | 'high_difficulty';

  const quickFilters: { value: QuickFilterType; label: string; count?: number }[] = [
    { value: 'all', label: 'All MCQs', count: mcqs.length },
    { value: 'pending', label: 'Pending Review', count: mcqs.filter(m => m.review_status === 'pending').length },
    { value: 'needs_revision', label: 'Needs Revision', count: mcqs.filter(m => m.review_status === 'needs_revision').length },
    { value: 'missing_explanation', label: 'Missing Explanation', count: mcqs.filter(m => !m.explanation).length },
    { value: 'recent', label: 'Recent (7 days)', count: mcqs.filter(m => {
      if (!m.last_reviewed_at) return false;
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return new Date(m.last_reviewed_at) >= sevenDaysAgo;
    }).length },
    { value: 'high_difficulty', label: 'High Difficulty', count: mcqs.filter(m => m.difficulty >= 4).length },
  ];

  // Exam label quick filters
  const examLabelQuickFilters = labels.map(label => ({
    labelId: label.id,
    label: `${label.name} MCQs`,
    count: mcqs.filter(m => m.exam_labels?.some(el => el.id === label.id)).length
  }));

  useEffect(() => {
    if (!editing) return;
    (async () => {
      const { data, error } = await sb.from("mcq_exam_labels").select("label_id").eq("mcq_id", editing.id);
      if (!error) setEditLabelIds((data || []).map((r: any) => r.label_id as string));
    })();
  }, [editing]);

  const createMutation = useMutation({
    mutationFn: async (payload: Omit<Mcq, "id">) => {
      const { data, error } = await sb.from("mcqs").insert(payload).select("id").maybeSingle();
      if (error) throw error;
      const mcqId = data?.id as string | undefined;
      if (mcqId && createLabelIds.length) {
        const rows = createLabelIds.map((label_id) => ({ mcq_id: mcqId, label_id }));
        const { error: mapErr } = await sb.from("mcq_exam_labels").insert(rows);
        if (mapErr) throw mapErr;
      }
    },
    onSuccess: async () => {
      toast({ title: "MCQ created" });
      setCreateLabelIds([]);
      await qc.invalidateQueries({ queryKey: ["all-mcqs"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to create MCQ", description: e?.message ?? "An error occurred", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }: Mcq) => {
      const { error } = await sb.from("mcqs").update(payload).eq("id", id);
      if (error) throw error;
      // Sync labels: replace existing with current selection
      const { error: delErr } = await sb.from("mcq_exam_labels").delete().eq("mcq_id", id);
      if (delErr) throw delErr;
      if (editLabelIds.length) {
        const rows = editLabelIds.map((label_id) => ({ mcq_id: id, label_id }));
        const { error: insErr } = await sb.from("mcq_exam_labels").insert(rows);
        if (insErr) throw insErr;
      }
    },
    onSuccess: async () => {
      toast({ title: "MCQ updated" });
      setEditing(null);
      await qc.invalidateQueries({ queryKey: ["all-mcqs"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to update MCQ", description: e?.message ?? "An error occurred", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await sb.from("mcqs").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      toast({ title: "MCQ deleted" });
      await qc.invalidateQueries({ queryKey: ["all-mcqs"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to delete MCQ", description: e?.message ?? "An error occurred", variant: "destructive" });
    },
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <Card>
        <CardHeader>
          <CardTitle>Add new MCQ</CardTitle>
        </CardHeader>
        <CardContent>
          <MCQForm
            topics={topics}
            submitting={createMutation.isPending}
            examLabels={labels}
            examLabelIds={createLabelIds}
            onLabelsChange={setCreateLabelIds}
            onSubmit={(payload) =>
              createMutation.mutate({ ...payload, explanation: payload.explanation ?? null } as Omit<Mcq, "id">)
            }
            submitLabel="Create"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <CardTitle>Existing MCQs ({filtered.length})</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset Filters
              </Button>
            </div>

            {/* Quick Filters */}
            <div className="flex flex-wrap gap-2">
              {quickFilters.map((filter) => (
                <Button
                  key={filter.value}
                  variant={quickFilter === filter.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setQuickFilter(filter.value)}
                  className="gap-2"
                >
                  {filter.label}
                  {filter.count !== undefined && (
                    <Badge variant="secondary" className="ml-1">
                      {filter.count}
                    </Badge>
                  )}
                </Button>
              ))}
              
              {/* Exam Label Quick Filters */}
              {examLabelQuickFilters.map((filter) => (
                <Button
                  key={filter.labelId}
                  variant={examLabelFilter.includes(filter.labelId) ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setExamLabelFilter(prev => 
                      prev.includes(filter.labelId) 
                        ? prev.filter(id => id !== filter.labelId)
                        : [...prev, filter.labelId]
                    );
                  }}
                  className="gap-2"
                >
                  {filter.label}
                  <Badge variant="secondary" className="ml-1">
                    {filter.count}
                  </Badge>
                </Button>
              ))}
            </div>

            {/* Advanced Filters */}
            <div className="flex w-full flex-col gap-2 sm:flex-row">
              <div className="flex-1">
                <Input placeholder="Search questions..." value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>
              <TopicSelect topics={topics} value={topicFilter} onChange={setTopicFilter} />
              <DifficultySelect value={difficultyFilter} onChange={setDifficultyFilter} />
              <select
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                value={reviewStatusFilter || ""}
                onChange={(e) => setReviewStatusFilter(e.target.value || undefined)}
              >
                <option value="">All Reviews</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="needs_revision">Needs Revision</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="text-destructive">Failed to load MCQs.</div>
          ) : mcqs.length === 0 ? (
            <div className="text-muted-foreground">No MCQs yet.</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No MCQs match your filters.</p>
              <Button variant="link" onClick={resetFilters} className="mt-2">
                Clear all filters
              </Button>
            </div>
          ) : (
            <MCQTable
              mcqs={filtered}
              topics={byId}
              selectedIds={selectedMcqIds}
              onSelectionChange={setSelectedMcqIds}
              onEdit={setEditing}
              onPreview={setPreviewMcq}
              onDuplicate={handleDuplicate}
              onDelete={(mcq) => deleteMutation.mutate(mcq.id)}
              onGenerateExplanation={setExplanationMcq}
              onAIReview={setReviewMcq}
            />
          )}
        </CardContent>
      </Card>

      <BulkActionsToolbar
        selectedCount={selectedMcqIds.size}
        onClearSelection={() => setSelectedMcqIds(new Set())}
        onAddLabels={handleBulkAddLabels}
        onRemoveLabels={handleBulkRemoveLabels}
      />

      <BulkLabelActionsDialog
        open={showBulkLabelDialog}
        onOpenChange={setShowBulkLabelDialog}
        selectedMcqIds={Array.from(selectedMcqIds)}
        mode={bulkLabelMode}
        onComplete={handleBulkComplete}
      />

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit MCQ</DialogTitle>
          </DialogHeader>
          {editing && (
            <MCQForm
              topics={topics}
              initial={editing}
              submitting={updateMutation.isPending}
              examLabels={labels}
              examLabelIds={editLabelIds}
              onLabelsChange={setEditLabelIds}
              onSubmit={(payload) =>
                updateMutation.mutate({ id: editing.id, ...payload, explanation: payload.explanation ?? null } as Mcq)
              }
              onCancel={() => setEditing(null)}
              submitLabel="Update"
            />
          )}
        </DialogContent>
      </Dialog>

      {explanationMcq && (
        <ExplanationGenerator
          mcq={explanationMcq}
          isOpen={!!explanationMcq}
          onClose={() => setExplanationMcq(null)}
          topicName={explanationMcq.topic_id ? byId.get(explanationMcq.topic_id)?.name : undefined}
        />
      )}

      <MCQPreviewModal
        mcq={previewMcq}
        topicName={previewMcq?.topic_id ? byId.get(previewMcq.topic_id)?.name : undefined}
        open={!!previewMcq}
        onClose={() => setPreviewMcq(null)}
        onEdit={() => {
          if (previewMcq) {
            setEditing(previewMcq);
            setPreviewMcq(null);
          }
        }}
      />

      <MCQReviewDialog
        mcq={reviewMcq ? {
          id: reviewMcq.id,
          stem: reviewMcq.question,
          option_a: reviewMcq.options[0] || '',
          option_b: reviewMcq.options[1] || '',
          option_c: reviewMcq.options[2] || '',
          option_d: reviewMcq.options[3] || null,
          option_e: reviewMcq.options[4] || null,
          correct_option: ['A', 'B', 'C', 'D', 'E'][reviewMcq.correct_index] as 'A' | 'B' | 'C' | 'D' | 'E',
          explanation: reviewMcq.explanation
        } : null}
        open={!!reviewMcq}
        onClose={() => setReviewMcq(null)}
        onReviewComplete={() => qc.invalidateQueries({ queryKey: ["all-mcqs"] })}
      />
    </div>
  );
};

export default MCQManager;
